import React, { useState, useEffect, useRef } from 'react'
import map from 'lodash/map'
import find from 'lodash/find'
import filter from 'lodash/filter'
import classnames from 'classnames'
import ButtonParameter from './ButtonParameter'
import ReviewForRepurposeParameter from './ReviewForRepurposeParameter'
import ReviewForDubbingParameter from './ReviewForDubbingParameter'
import EnhanceAudioParameter from './EnhanceAudioParameter'
import ManualReview from '@Images/finetune_parameters_manual_review.svg'
import FastMode from '@Images/finetune_parameters_fast_mode.svg'
import Style from '@Images/finetune_parameters_style.svg'
import Vocabulary from '@Images/finetune_parameters_vocabulary.svg'
import Termbase from '@Images/finetune_parameters_termbase.svg'
import SpeakerDetection from '@Images/finetune_parameters_speaker_detection.svg'
import AutoTone from '@Images/finetune_parameters_auto_tone.svg'
import FormalTone from '@Images/finetune_parameters_formal_tone.svg'
import InformalTone from '@Images/finetune_parameters_informal_tone.svg'
import Voice from '@Images/finetune_parameters_voice.svg'
import NoVoice from '@Images/finetune_parameters_no_voice.svg'
import LipSync from '@Images/finetune_parameters_lip_sync.svg'
import Rephrasing from '@Images/finetune_parameters_rephrasing.svg'
import IsolateVoices from '@Images/finetune_parameters_isolate_voices.svg'
import OutputFormat from '@Images/finetune_parameters_output_format.svg'
import Arrow from '@Images/finetune_parameters_dropdown_arrow.svg'

import {
  getVoiceCloningEngineButtonLabel,
  getVoiceCloningEngineId,
  getVoiceCloningEngineOptionsForLanguages,
  ODUB_MODEL_ID,
  STANDARD_TTS_MODEL_ID,
  STANDARD_TTS_MODEL_LABEL,
} from '@Utils/OdubTtsModels'

import {
  IProjectVersionSettingsParameter,
} from '@SiteModules/ProjectVersionSettings/types'

import {
  IVocabulary,
} from '@SiteModules/Vocabulary/types'

import {
  ITermbase,
} from '@SiteModules/Termbase/types'

import {
  ITheme,
} from '@SiteModules/Theme/types'

import './Parameters.scss'

const OUTPUT_FORMATS = [{
  displayedOptionDropdown: '📱 9:16 (Portrait) • TikTok, Reels, Shorts',
  displayedOptionButton: 'Clip format: 9:16',
  databaseValue: 'format_9_16'
}, {
  displayedOptionDropdown: '💻 16:9 (Landscape) • YouTube, Web',
  displayedOptionButton: 'Clip format: 16:9',
  databaseValue: 'format_16_9'
}, {
  displayedOptionDropdown: '⬜ 1:1 (Square) • LinkedIn, Insta Feed',
  displayedOptionButton: 'Clip format: 1:1',
  databaseValue: 'format_1_1'
}]

interface ParametersProps {
  readonly isNewSubtitlesProjectWithoutTranslation: boolean
  readonly isNewDubbingProject: boolean
  readonly isAddingLanguagesToExistingProject: boolean
  readonly isAddingClipsToExistingProject: boolean
  readonly isDubbingProject: boolean
  readonly isSubtitlesProject: boolean
  readonly isAddingClipsToExistingProjectWithoutTranslation: boolean
  readonly reviewModeOption: string | null
  readonly reviewModeOptionsForRepurpose: string[]
  readonly reviewModeOptionsForDubbing: string[]
  readonly styleOption: string | null
  readonly vocabularyOption: string | null
  readonly termbaseOption: string | null
  readonly speakerDetectionOption: string | null
  readonly translationToneOption: string | null
  readonly dubbingModeOption: string | null
  readonly odubModelOption: string | null
  readonly dubbingTargetLanguages: string[]
  readonly rephrasingForDubbingOption: string | null
  readonly isolateVoicesOption: string | null
  readonly lipSyncOption: string | null
  readonly enhanceAudioOptions: string[]
  readonly outputFormatOption: string
  readonly themes: ITheme[]
  readonly vocabularies: IVocabulary[]
  readonly termbases: ITermbase[]
  readonly currentAccountSubscriptionAllowsVoiceCloning: boolean
  readonly shouldRepurpose: boolean
  readonly shouldDub: boolean
  /** True when the user has AI dubbing turned on; used for audio enhancement UI only. */
  readonly aiDubbingEnabled: boolean
  readonly isForAdvancedParameters: boolean
  readonly willTranslate: boolean
  readonly hasNewTranscription: boolean
  setReviewModeOption(newReviewModeOption: string | null): void
  setReviewModeOptionsForRepurpose(newReviewModeOptionsForRepurpose: string[]): void
  setReviewModeOptionsForDubbing(newReviewModeOptionsForDubbing: string[]): void
  setStyleOption(newStyleOption: string | null): void
  setVocabularyOption(newVocabularyOption: string | null): void
  setTermbaseOption(newTermbaseOption: string | null): void
  setSpeakerDetectionOption(newSpeakerDetectionOption: string | null): void
  setTranslationToneOption(newTranslationToneOption: string | null): void
  setDubbingModeOption(newDubbingModeOption: string | null): void
  setOdubModelOption(newOdubModelOption: string | null): void
  setRephrasingForDubbingOption(newRephrasingForDubbingOption: string | null): void
  setIsolateVoicesOption(newIsolateVoicesOption: string | null): void
  setLipSyncOption(newLipSyncOption: string | null): void
  setEnhanceAudioOptions(newEnhanceAudioOptions: string[]): void
  setOutputFormatOption(newOutputFormatOption: string): void
  setShowStylingPopup(newShowStylingPopup: boolean): void
}

const Parameters: React.FC<ParametersProps> = (props) => {
  const {
    isNewSubtitlesProjectWithoutTranslation,
    isNewDubbingProject,
    reviewModeOption,
    styleOption,
    vocabularyOption,
    termbaseOption,
    speakerDetectionOption,
    translationToneOption,
    dubbingModeOption,
    odubModelOption,
    dubbingTargetLanguages,
    rephrasingForDubbingOption,
    isolateVoicesOption,
    lipSyncOption,
    enhanceAudioOptions,
    outputFormatOption,
    setReviewModeOption,
    setStyleOption,
    setVocabularyOption,
    setTermbaseOption,
    setSpeakerDetectionOption,
    setTranslationToneOption,
    setDubbingModeOption,
    setOdubModelOption,
    setIsolateVoicesOption,
    setRephrasingForDubbingOption,
    setLipSyncOption,
    setEnhanceAudioOptions,
    setOutputFormatOption,
    themes,
    vocabularies,
    termbases,
    setShowStylingPopup,
    currentAccountSubscriptionAllowsVoiceCloning,
    shouldRepurpose,
    shouldDub,
    isForAdvancedParameters,
    isAddingLanguagesToExistingProject,
    isAddingClipsToExistingProject,
    isSubtitlesProject,
    isDubbingProject,
    isAddingClipsToExistingProjectWithoutTranslation,
    willTranslate,
    hasNewTranscription,
    reviewModeOptionsForRepurpose,
    setReviewModeOptionsForRepurpose,
    reviewModeOptionsForDubbing,
    setReviewModeOptionsForDubbing,
  } = props

  const [showAdvancedParameters, setShowAdvancedParameters] = useState(false)
  const advancedButtonsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!advancedButtonsRef.current) { return }

    advancedButtonsRef.current.style.height = showAdvancedParameters ? `${advancedButtonsRef.current.scrollHeight}px` : '0px'
  }, [showAdvancedParameters, enhanceAudioOptions.length])

  const handleSetStyleOption = (newOption: string | null) => {
    if (!newOption) {
      setStyleOption(null)
    } else {
      const adjustedNewOption = newOption.replace(' style', '')
      setStyleOption(adjustedNewOption)
    }
  }

  const handleSetOdubModelOption = (displayName: string | null) => {
    setOdubModelOption(displayName?.startsWith('✨ ') ? displayName.slice(2) : displayName)
  }

  const handleSetOutputFormatOption = (newOption: string) => {
    const adjustedNewOption = find(OUTPUT_FORMATS, format => format.displayedOptionDropdown === newOption)?.databaseValue

    if (adjustedNewOption) {
      setOutputFormatOption(adjustedNewOption)
    }
  }

  const openStylingPopup = () => {
    setShowStylingPopup(true)
  }

  const toggleAdvancedParameters = () => {
    setShowAdvancedParameters(!showAdvancedParameters)
  }

  const reviewModeParameter: IProjectVersionSettingsParameter = {
    name: 'Review mode',
    options: [{
      name: 'Review transcript (best quality)',
      icon: <ManualReview />,
      description: 'Review and correct the transcript before translations are generated.'
    }, {
      name: 'Fast translation',
      icon: <FastMode />,
      description: 'Generate translations directly without transcript review.'
    }],
    selectedOptionName: reviewModeOption,
    selectedOptionNameButton: reviewModeOption === 'Review transcript (best quality)' ? 'Review transcript' : reviewModeOption,
    setSelectedOption: setReviewModeOption,
    shouldHide: shouldRepurpose || shouldDub || isNewSubtitlesProjectWithoutTranslation || isAddingLanguagesToExistingProject,
    showAsAdvanced: false
  }

  const styleParameter: IProjectVersionSettingsParameter = {
    name: 'Subtitle style',
    icon: <Style />,
    searchPlaceholder: 'Select style...',
    options: map(themes, theme => ({ name: theme.attributes.name + ' style' })),
    nullOption: {
      name: 'Default style',
    },
    selectedOptionName: styleOption ? styleOption + ' style' : null,
    setSelectedOption: handleSetStyleOption,
    shouldHide: false,
    showAsAdvanced: isDubbingProject || shouldRepurpose,
    isActive: !!styleOption,
    specialButtonClickBehaviour: openStylingPopup,
    tooltipText: 'Select a subtitle style to enhance your video with customized text style and animation.'
  }

  const vocabularyParameter: IProjectVersionSettingsParameter = {
    name: 'Vocabulary',
    icon: <Vocabulary />,
    searchPlaceholder: 'Select vocabulary...',
    options: map(vocabularies, vocabulary => ({ name: vocabulary.attributes.name, number: vocabulary.attributes.utterances ? vocabulary.attributes.utterances.length : 0 })),
    nullOption: {
      name: 'No vocabulary',
    },
    selectedOptionName: vocabularyOption,
    setSelectedOption: setVocabularyOption,
    shouldHide: isAddingLanguagesToExistingProject || isAddingClipsToExistingProject,
    showAsAdvanced: true,
    isActive: !!vocabularyOption,
    tooltipText: 'Add custom terms, names, or technical words to improve pronunciation accuracy and ensure proper translation of specialized content.'
  }

  const termbaseParameter: IProjectVersionSettingsParameter = {
    name: 'Term base',
    icon: <Termbase />,
    searchPlaceholder: 'Select term base...',
    options: map(termbases, termbase => ({ name: termbase.attributes.name })),
    nullOption: {
      name: 'No term base',
    },
    selectedOptionName: termbaseOption,
    setSelectedOption: setTermbaseOption,
    shouldHide: isAddingClipsToExistingProjectWithoutTranslation,
    showAsAdvanced: true,
    isActive: !!termbaseOption,
    tooltipText: 'Use a glossary of predefined translations to ensure consistent terminology and/or pronunciation across your project.'
  }

  const dubbingModeParameter: IProjectVersionSettingsParameter = {
    name: 'Dubbing mode',
    options: [{
      name: 'Voice-cloning',
      icon: <Voice />,
      ...(!currentAccountSubscriptionAllowsVoiceCloning ? { trailingIcon: <span className="Parameters__trailing-icon">Upgrade</span> } : {})
    }],
    nullOption: {
      name: 'No dubbing',
      icon: <NoVoice />
    },
    selectedOptionName: dubbingModeOption,
    setSelectedOption: setDubbingModeOption,
    shouldHide: isSubtitlesProject,
    showAsAdvanced: false,
    tooltipText: 'Enable voice cloning to dub with custom voices, or disable to use standard library voices later.'
  }

  const odubModelParameter: IProjectVersionSettingsParameter = {
    name: 'Voice cloning engine',
    options: [...getVoiceCloningEngineOptionsForLanguages(dubbingTargetLanguages)].map(option => ({
      name: option.id === ODUB_MODEL_ID ? `✨ ${option.label}` : option.label,
      description: option.description,
    })),
    selectedOptionName: getVoiceCloningEngineId(odubModelOption) === ODUB_MODEL_ID ? `✨ ${odubModelOption}` : odubModelOption,
    selectedOptionNameButton: getVoiceCloningEngineButtonLabel(odubModelOption ? getVoiceCloningEngineId(odubModelOption) : STANDARD_TTS_MODEL_ID),
    dropdownDirection: 'up',
    setSelectedOption: handleSetOdubModelOption,
    shouldHide: isSubtitlesProject || dubbingModeOption !== 'Voice-cloning',
    showAsAdvanced: true,
    isActive: odubModelOption !== STANDARD_TTS_MODEL_LABEL,
    tooltipText: 'Choose whether to use the default TTS provider or the automatic odub voice cloning model.'
  }

  const lipSyncParameter: IProjectVersionSettingsParameter = {
    name: 'Lip-sync',
    icon: <LipSync />,
    largeIcon: true,
    options: [{
      name: 'Fox model',
    }, {
      name: 'Tiger model',
    }, {
      name: 'Dragon model',
    }],
    nullOption: {
      name: 'No lip-sync',
    },
    selectedOptionName: lipSyncOption,
    setSelectedOption: setLipSyncOption,
    shouldHide: isSubtitlesProject,
    showAsAdvanced: true,
    isActive: !!lipSyncOption,
    tooltipText: `Enable lip movement generation to synchronize on-screen actors' mouth movements with the dubbed audio for more realistic results.`
  }

  const speakerDetectionParameter: IProjectVersionSettingsParameter = {
    name: 'Speaker detection',
    icon: <SpeakerDetection />,
    options: [{
      name: 'One speaker',
    }, {
      name: 'Multiple speakers',
    }],
    selectedOptionName: speakerDetectionOption,
    setSelectedOption: setSpeakerDetectionOption,
    shouldHide: isAddingLanguagesToExistingProject || isAddingClipsToExistingProject,
    showAsAdvanced: isNewDubbingProject || shouldRepurpose,
    isActive: speakerDetectionOption !== 'Multiple speakers',
    tooltipText: 'Choose single speaker mode for content with one voice, or multi-speaker mode to automatically identify different speakers.'
  }

  const translationToneParameter: IProjectVersionSettingsParameter = {
    name: 'Translation tone',
    options: [{
      name: 'Auto tone',
      icon: <AutoTone />
    }, {
      name: 'Formal tone',
      icon: <FormalTone />
    }, {
      name: 'Informal tone',
      icon: <InformalTone />
    }],
    selectedOptionName: translationToneOption,
    setSelectedOption: setTranslationToneOption,
    shouldHide: isNewSubtitlesProjectWithoutTranslation || isAddingClipsToExistingProjectWithoutTranslation,
    showAsAdvanced: true,
    isActive: translationToneOption !== 'Auto tone',
    tooltipText: 'Select the translation tone style: formal for professional content or informal for casual or conversational content.'
  }

  const rephrasingForDubbingParameter: IProjectVersionSettingsParameter = {
    name: 'Rephrasing for dubbing',
    icon: <Rephrasing />,
    options: [{
      name: 'Rephrase for dubbing',
    }],
    nullOption: {
      name: 'No rephrasing',
    },
    selectedOptionName: rephrasingForDubbingOption,
    setSelectedOption: setRephrasingForDubbingOption,
    shouldHide: isSubtitlesProject,
    showAsAdvanced: true,
    isActive: rephrasingForDubbingOption !== 'Rephrase for dubbing',
    tooltipText: 'Adapt translations to match the timing and lip movements of the original dialogue for better synchronization.'
  }

  const isolateVoicesParameter: IProjectVersionSettingsParameter = {
    name: 'Isolate voices',
    icon: <IsolateVoices />,
    options: [{
      name: 'No voice isolation',
    }, {
      name: 'Isolate voices',
    }],
    selectedOptionName: isolateVoicesOption,
    setSelectedOption: setIsolateVoicesOption,
    shouldHide: isSubtitlesProject,
    showAsAdvanced: true,
    isActive: isolateVoicesOption !== 'Isolate voices',
    tooltipText: 'Enable voice isolation to separate dialogue from background audio, music, and sound effects for cleaner dubbing results.'
  }

  const outputFormatParameter: IProjectVersionSettingsParameter = {
    name: 'Clip format',
    icon: <OutputFormat />,
    options: map(OUTPUT_FORMATS, format => {
      return (
        { name: format.displayedOptionDropdown }
      )
    }),
    selectedOptionName: find(OUTPUT_FORMATS, format => format.databaseValue === outputFormatOption)?.displayedOptionDropdown || '',
    selectedOptionNameButton:  find(OUTPUT_FORMATS, format => format.databaseValue === outputFormatOption)?.displayedOptionButton || '',
    setSelectedOption: handleSetOutputFormatOption,
    shouldHide: !shouldRepurpose,
    showAsAdvanced: false,
    tooltipText: `Select the output format for your clips.`
  }

  const allParameters = [
    outputFormatParameter,
    reviewModeParameter,
    styleParameter,
    vocabularyParameter,
    termbaseParameter,
    dubbingModeParameter,
    odubModelParameter,
    lipSyncParameter,
    speakerDetectionParameter,
    translationToneParameter,
    rephrasingForDubbingParameter,
    isolateVoicesParameter,
  ]

  const allPossibleAdvancedParameters = filter(allParameters, parameter => typeof(parameter.showAsAdvanced) === 'boolean')
  const advancedParameters = filter(allPossibleAdvancedParameters, parameter => !!parameter.showAsAdvanced && !parameter.shouldHide)
  const standardParameters = filter(allPossibleAdvancedParameters, parameter => !parameter.showAsAdvanced && !parameter.shouldHide)
  const showEnhanceAudioParameter = !isAddingLanguagesToExistingProject || isDubbingProject
  const enhanceAudioParameterActive = enhanceAudioOptions.length > 0
  const hasAdvancedParameters = advancedParameters.length > 0 || showEnhanceAudioParameter
  const numberOfActiveAdvancedParameters =
    filter(advancedParameters, advancedParameter => advancedParameter.isActive).length +
    (showEnhanceAudioParameter && enhanceAudioParameterActive ? 1 : 0)

  const standardButtonParameters = map(standardParameters, (parameter, index) => {
    return (
      <ButtonParameter
        parameter={parameter}
        key={index}
        classNameOfContainerToMakeUnscrollable="SetupProjectPopup"
      />
    )
  })

  const advancedButtonParameters = map(advancedParameters, (parameter, index) => {
    return (
      <ButtonParameter
        parameter={parameter}
        key={index}
        classNameOfContainerToMakeUnscrollable="SetupProjectPopup"
      />
    )
  })

  const renderStandardButtonParameters = () => {
    return (
      <React.Fragment>
        {!shouldRepurpose && (
          <div className="SetupProjectPopup__section-header">
            Parameters
          </div>
        )}
        <div className="Parameters__buttons">
          {standardButtonParameters}
          {shouldRepurpose &&
            <ReviewForRepurposeParameter
              willTranslate={willTranslate}
              hasNewTranscription={hasNewTranscription}
              reviewModeOptionsForRepurpose={reviewModeOptionsForRepurpose}
              setReviewModeOptionsForRepurpose={setReviewModeOptionsForRepurpose}
            />
          }
          {shouldDub &&
            <ReviewForDubbingParameter
              hasNewTranscription={hasNewTranscription}
              reviewModeOptionsForDubbing={reviewModeOptionsForDubbing}
              setReviewModeOptionsForDubbing={setReviewModeOptionsForDubbing}
            />
          }
        </div>
      </React.Fragment>
    )
  }

  const renderAdvancedButtonParameters = () => {
    return (
      <React.Fragment>
        {hasAdvancedParameters && (
          <React.Fragment>
            <div
              className="SetupProjectPopup__section-header Parameters__advanced-header"
              onClick={toggleAdvancedParameters}
            >
              Advanced Settings
              <span className="Parameters__advanced-active">
                ({numberOfActiveAdvancedParameters} active)
              </span>
              <Arrow
                className={classnames(
                  'Parameters__advanced-arrow',
                  { 'Parameters__advanced-arrow--rotated': showAdvancedParameters }
                )}
              />
            </div>
            <div
              className={classnames(
                'Parameters__buttons',
                { 'Parameters__buttons-hidden': !showAdvancedParameters }
              )}
              ref={advancedButtonsRef}
            >
              {advancedButtonParameters}
              {showEnhanceAudioParameter && (
                <EnhanceAudioParameter
                  enhanceAudioOptions={enhanceAudioOptions}
                  setEnhanceAudioOptions={setEnhanceAudioOptions}
                />
              )}
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    )
  }

  return (
    <div className="Parameters">
      {isForAdvancedParameters ? renderAdvancedButtonParameters() : renderStandardButtonParameters()}
    </div>
  )
}

export default Parameters
